import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, ComposedChart,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import { useIsMobile } from "./responsive";
import { buildMarketMetrics, periodLabel, volumeTotals, koFilterOptions, filterKoCampa } from "./Kocampaparse";
import BrandView from "./Brandview";

const T = {
  surface: "#FFFFFF", bgAlt: "#FFF8F8", border: "#FADDDD", borderMed: "#F5BABA",
  accent: "#E8001A", accentDark: "#B50015", accentDeep: "#800010", accentSoft: "#FFF0F0",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", warn: "#B06000", ink: "#12100E",
  shadow: "0 2px 8px rgba(232,0,26,0.08)", shadowMd: "0 4px 16px rgba(232,0,26,0.12)",
};

const BRAND_COLORS = ["#E8001A", "#1A7A9A", "#B06000", "#5A8A2A", "#7A3A8A",
                      "#C42020", "#0A5A7A", "#E8701A", "#3A6A9A", "#8A2E5E"];

/* ── Formatters ──────────────────────────────────────────────────────── */
const fmtVol = (v) => {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toFixed(0);
};
const fmtPct  = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d)}%`);
const fmtSign = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(d)}%`);
const gColor  = (v) => (v == null ? T.textDim : v > 0.0005 ? T.pos : v < -0.0005 ? T.neg : T.textDim);

const Tip = ({ active, payload, label, pct }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
      borderRadius: 8, padding: "10px 14px", fontSize: 11, boxShadow: T.shadowMd }}>
      {label && <div style={{ color: T.textDim, marginBottom: 6, fontSize: 10,
        textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill }} />
            <span style={{ color: T.textMid }}>{p.name}</span>
          </span>
          <span style={{ color: T.text, fontWeight: 600 }}>
            {pct ? fmtPct(Number(p.value)) : fmtVol(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

function Card({ title, subtitle, badge, children, style = {} }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: 16, display: "flex", flexDirection: "column", boxShadow: T.shadow, ...style }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{title}</div>
          {subtitle && <div style={{ color: T.textDim, fontSize: 10.5, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {badge && <div style={{ background: T.accentSoft, border: `1px solid ${T.borderMed}`,
          color: T.accent, fontSize: 9, padding: "3px 8px", borderRadius: 4, fontWeight: 700 }}>{badge}</div>}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

/* Inline bullet bar — the deck's signature "bar inside a table cell" */
/* fill — the bar stretches to the cell's width instead of a fixed size, so
   the table uses its space for the bars rather than leaving empty gaps. */
function Bullet({ value, max, color, label, width = 96, fill = false }) {
  const pct = max ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
      <div style={{ width: fill ? undefined : width, flex: fill ? 1 : undefined,
        minWidth: fill ? 40 : undefined, height: 9, background: "#EFEFEF", borderRadius: 3,
        overflow: "hidden", flexShrink: fill ? 1 : 0 }}>
        <div style={{ width: `${pct * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ minWidth: 44, textAlign: "right", fontSize: 11, fontWeight: 600, color: T.text }}>{label}</span>
    </div>
  );
}

/* KPI tile definitions for the volume view. `delta` is a growth fraction;
   `deltaPts` a change in share points; both compare with the same period
   last year. Exported so the Highlights page renders identical tiles. */
export function volumeTiles(t) {
  const vs = t.hasPrev ? `vs ${t.prevLabel}` : "no prior-year data";
  return [
    { id: "koTotal", label: "KO India Sparkling Volume", value: fmtVol(t.koTotal),
      color: T.accent, icon: "🥤", delta: t.koTotalGrowth, deltaLabel: vs },
    { id: "koAff", label: "KO Affordable (PET)", value: fmtVol(t.koAff),
      color: T.accentDark, icon: "💰", delta: t.koAffGrowth, deltaLabel: vs },
    { id: "affMix", label: "Affordable PET Mix", value: fmtPct(t.affMix),
      color: T.pos, icon: "📊", deltaPts: t.affMixDeltaPts, deltaLabel: vs },
    { id: "campa", label: "Campa Volume", value: fmtVol(t.campa),
      color: "#7B4FC4", icon: "⚠️", sub: t.curLabel },
    { id: "contribution", label: "Affordable Contribution to growth",
      value: t.contribution == null ? "\u2014" : fmtPct(t.contribution, 0),
      color: T.warn, icon: "⚔️",
      sub: t.totalDidNotGrow
        ? `total KO volume did not grow vs ${t.prevLabel}`
        : `share of incremental KO volume, ${t.curLabel} vs ${t.prevLabel}` },
  ];
}

export function VolTile({ label, value, color, icon, sub, delta, deltaPts, deltaLabel }) {
  const d = delta ?? deltaPts;
  const txt = delta != null
    ? `${Math.abs(delta * 100).toFixed(1)}%`
    : deltaPts != null ? `${Math.abs(deltaPts * 100).toFixed(2)} pts` : null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: 16, boxShadow: T.shadow, borderTop: `3px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div style={{ color: T.textDim, fontSize: 10, letterSpacing: 1.2,
          textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
        <span style={{ fontSize: 18, opacity: 0.25 }}>{icon}</span>
      </div>
      <div style={{ color: T.text, fontSize: 22, fontWeight: 700, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ color: T.textDim, fontSize: 9.5, marginTop: 3 }}>{sub}</div>}
      {txt && (
        <div style={{ marginTop: 10 }}>
          <span style={{ color: d >= 0 ? T.pos : T.neg, fontSize: 11, fontWeight: 700,
            padding: "2px 8px", borderRadius: 4,
            background: d >= 0 ? "rgba(26,122,58,0.12)" : "rgba(196,32,32,0.1)" }}>
            {d >= 0 ? "▲" : "▼"} {txt}
          </span>
          <span style={{ color: T.textDim, fontSize: 10, marginLeft: 6 }}>{deltaLabel}</span>
        </div>
      )}
    </div>
  );
}

/* Ratios above ~2x read better as "6X" than "600%" — the deck does this too */
const respLabel = (r) => {
  if (r == null) return "\u2014";
  return r >= 2 ? `${Math.round(r)}X` : `${(r * 100).toFixed(0)}%`;
};

const Pill = ({ v }) => (
  <span style={{
    display: "inline-block", minWidth: 52, textAlign: "center",
    padding: "2px 8px", borderRadius: 10, fontSize: 10.5, fontWeight: 700,
    color: v == null ? T.textDim : v >= 0 ? "#0F5C2A" : "#8E1414",
    background: v == null ? "#F2F2F2" : v >= 0 ? "#D9F0E1" : "#FBDADA",
  }}>{fmtSign(v, 0)}</span>
);

/* ══════════════════════════════════════════════════════════════════════ */
export default function KoCampaView({ data: rawData, brand = null, onAskAI }) {
  const { months, brands = [], packs = [] } = rawData;
  const isMobile = useIsMobile();
  /* Entity (bottler) and BU Ship From filters. BU options narrow to the
     chosen entity; picking a new entity resets an out-of-scope BU. */
  const [entitySel, setEntitySel] = useState("__all__");
  const [buSel, setBuSel] = useState("__all__");
  const opts = useMemo(() => koFilterOptions(rawData), [rawData]);
  const buOptions = useMemo(() => entitySel === "__all__" ? opts.buShipFrom
    : koFilterOptions({ koRows: rawData.koRows.filter(r => r.entity === entitySel) }).buShipFrom,
    [rawData, opts, entitySel]);
  const data = useMemo(() => filterKoCampa(rawData, { entity: entitySel, bu: buSel }),
    [rawData, entitySel, buSel]);
  const [month, setMonth]   = useState(months[months.length - 1] || "");
  const [metric, setMetric] = useState("ko");      // scatter x-axis — total KO volume by default
  // YTD by default: "Aug" = Jan–Aug of the current year, like the deck
  const [basis, setBasis] = useState("ytd");
  const period = periodLabel(data, month, basis);

  const markets = useMemo(
    () => buildMarketMetrics(data, month, { basis }).sort((a, b) => b.campa - a.campa),
    [data, month, basis]
  );

  /* All-India totals for the KPI strip */
  // Same helper the Highlights page uses, so both show identical figures
  const totals = useMemo(() => volumeTotals(data, month, basis), [data, month, basis]);

  const maxCampa = Math.max(...markets.map(m => m.campa), 1);
  const maxMix = Math.max(...markets.map(m => m.affMix), 0.05);

  /* The deck splits franchise bottlers from company markets with a rule,
     and outlines the biggest Campa market inside each block. */
  const groupedMarkets = useMemo(() => {
    const FRANCHISE = /slmg|moon|kgb|lbpl|bbpl|dbpl|sssl|kandhari|superior|bengal|diamond|ludhiana|waves|dankuni|taratala|rajmundry/i;
    const mk = (rows) => ({
      rows,
      peakKey: rows.reduce((best, r) => (!best || r.campa > best.campa ? r : best), null)?.key,
    });
    const fr = markets.filter(m => FRANCHISE.test(m.market));
    const co = markets.filter(m => !FRANCHISE.test(m.market));
    return [fr.length && mk(fr), co.length && mk(co)].filter(Boolean);
  }, [markets]);
  const medCampa = markets.length ? [...markets].sort((a, b) => a.campa - b.campa)[Math.floor(markets.length / 2)].campa : 0;
  const respVals = markets.map(m => m.response).filter(v => v != null);
  const medResp  = respVals.length ? [...respVals].sort((a, b) => a - b)[Math.floor(respVals.length / 2)] : 0;

  const scatter = markets.filter(m => m.campa > 0 && m.response != null).map(m => ({
    ...m,
    x: metric === "campa" ? m.campa : m.koTotal,
    y: Math.min(m.response * 100, 150),      // clamp to the axis ceiling
    yTrue: m.response * 100,                 // real value for the tooltip
    z: m.koAff,
  }));

  const darkSel = { background: "rgba(255,255,255,0.12)", color: "#fff",
    border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6, padding: "4px 8px",
    fontSize: 11, cursor: "pointer" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 16 }}>
      {/* ── Headline strip, styled after the deck's title bar ── */}
      <div style={{ background: T.ink, borderRadius: 10, padding: "14px 20px", color: "#fff",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>
          KO Affordable {fmtVol(totals.koAff)} UC · Campa {fmtVol(totals.campa)} UC ·
          Response {fmtPct(totals.response, 0)}
          <span style={{ fontWeight: 500, opacity: 0.65, marginLeft: 8, fontSize: 12 }}>· {period}</span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 999, overflow: "hidden" }}>
            {[["ytd", "YTD"], ["month", "Month"]].map(([id, lab]) => (
              <button key={id} onClick={() => setBasis(id)} style={{
                padding: "4px 12px", border: "none", cursor: "pointer", fontSize: 10.5, fontWeight: 700,
                background: basis === id ? "#fff" : "transparent",
                color: basis === id ? T.ink : "rgba(255,255,255,0.8)" }}>{lab}</button>
            ))}
          </div>
          {opts.entities.length > 1 && (<>
            <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>Entity</span>
            <select value={entitySel} onChange={e => { setEntitySel(e.target.value); setBuSel("__all__"); }} style={darkSel}>
              <option value="__all__" style={{ color: "#000" }}>All entities</option>
              {opts.entities.map(v => <option key={v} value={v} style={{ color: "#000" }}>{v}</option>)}
            </select>
          </>)}
          {opts.buShipFrom.length > 1 && (<>
            <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>BU Ship From</span>
            <select value={buSel} onChange={e => setBuSel(e.target.value)} style={{ ...darkSel, maxWidth: 200 }}>
              <option value="__all__" style={{ color: "#000" }}>All BUs</option>
              {buOptions.map(v => <option key={v} value={v} style={{ color: "#000" }}>{v}</option>)}
            </select>
          </>)}
          <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>Month</span>
          <select value={month} onChange={e => setMonth(e.target.value)}
            style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
            {months.map(m => <option key={m} value={m} style={{ color: "#000" }}>{m}</option>)}
          </select>
          {onAskAI && <button onClick={onAskAI} style={{ background: T.accent, border: "none", color: "#fff",
            padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🤖 Ask AI</button>}
        </div>
      </div>

      {/* ── KPI cards — growth vs the same period last year ── */}
      <div className="ddr-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        {volumeTiles(totals).map(k => <VolTile key={k.label} {...k} />)}
      </div>

      {/* ── Market scorecard, laid out like the source deck ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 16, boxShadow: T.shadow }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
            tableLayout: "fixed", fontSize: 11.5 }}>
            {/* Market is kept just wide enough for the names; the spare width
                goes to the two bar columns, where it is actually useful. */}
            <colgroup>
              <col style={{ width: "14%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "28%" }} />
              <col style={{ width: "13%" }} />
            </colgroup>
            <thead>
              <tr style={{ background: T.ink, color: "#fff" }}>
                <th style={{ ...thD, ...marketCell, textAlign: "left" }}>Market</th>
                <th style={thD}>
                  Campa Vol UC 0-20
                  <div style={subHd}>Projected Vol UC<br />Indexed till date</div>
                </th>
                <th style={thD}>Affordable<br />Total</th>
                <th style={thD}>Affordable<br />Mix w/n SPK</th>
                <th style={thD}>KO Competitive Response<br />as Percentage of Campa</th>
                <th style={thD}>Vol UC Growth<br />of 0-20 SPK
                  <div style={subHd}>vs same period last year</div></th>
              </tr>
            </thead>
            <tbody>
              {groupedMarkets.map((grp, gi) => grp.rows.map((m, ri) => {
                // The deck outlines the largest Campa market in each block
                const isPeak = m.key === grp.peakKey;
                return (
                  <tr key={m.key} style={{
                    borderTop: (gi && !ri) ? `2px solid ${T.borderMed}` : "none",
                  }}>
                    <td title={m.market}
                      style={{ ...tdD, ...marketCell, textAlign: "left", fontWeight: 700 }}>
                      {m.market}
                    </td>
                    <td style={tdD}>
                      <span style={isPeak ? peakBox : undefined}>{fmtVol(m.campa)}</span>
                    </td>
                    <td style={{ ...tdD, fontWeight: 700 }}>{fmtVol(m.koAff)}</td>
                    <td style={tdD}>
                      <Bullet value={m.affMix} max={maxMix} color={T.pos}
                        label={fmtPct(m.affMix)} fill />
                    </td>
                    <td style={tdD}>
                      <Bullet value={Math.min(m.response ?? 0, 1)} max={1} color={T.accent}
                        label={respLabel(m.response)} fill />
                    </td>
                    <td style={tdD}><Pill v={m.growth} /></td>
                  </tr>
                );
              }))}

              {/* ALL INDIA — outlined, exactly as the deck closes the table.
                  Built from ordinary cells (not a nested table) so the
                  columns stay aligned with the rows above. */}
              <tr style={{ background: "#FAFAFA" }}>
                <td style={{ ...tdD, ...marketCell, ...allIndiaCell, textAlign: "left",
                  fontWeight: 800, borderLeft: `2px solid ${T.accent}`,
                  borderTopLeftRadius: 6, borderBottomLeftRadius: 6 }}>ALL INDIA</td>
                <td style={{ ...tdD, ...allIndiaCell, fontWeight: 800 }}>{fmtVol(totals.campa)}</td>
                <td style={{ ...tdD, ...allIndiaCell, fontWeight: 800 }}>{fmtVol(totals.koAff)}</td>
                <td style={{ ...tdD, ...allIndiaCell }}>
                  <Bullet value={totals.affMix} max={maxMix} color={T.pos}
                    label={fmtPct(totals.affMix)} fill />
                </td>
                <td style={{ ...tdD, ...allIndiaCell }}>
                  <Bullet value={Math.min(totals.response ?? 0, 1)} max={1}
                    color={T.accent} label={respLabel(totals.response)} fill />
                </td>
                <td style={{ ...tdD, ...allIndiaCell, borderRight: `2px solid ${T.accent}`,
                  borderTopRightRadius: 6, borderBottomRightRadius: 6 }}>
                  <Pill v={totals.koGrowth} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legend strip, as printed under the deck's table */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 12,
          paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 10,
          color: T.textMid, alignItems: "center" }}>
          <span style={lg}><i style={{ ...sw, background: T.pos }} />Affordable Mix w/n SPK</span>
          <span style={lg}><i style={{ ...sw, background: T.accent }} />KO Competitive Response as % of Campa</span>
          <span style={lg}><i style={{ ...sw, background: "#D9F0E1", border: "1px solid #9BD3AF" }} />+ Positive</span>
          <span style={lg}><i style={{ ...sw, background: "#FBDADA", border: "1px solid #E9A8A8" }} />− Negative</span>
          <span style={{ marginLeft: "auto", color: T.accent, fontWeight: 700 }}>
            *KO {fmtVol(totals.koAff)}@10
          </span>
        </div>
      </div>

      {/* ── Threat quadrant ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
        <Card title="Competitive Threat Quadrant" badge="BUBBLE"
          subtitle="Campa scale × KO response · bubble = KO affordable volume">
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["campa", "vs Campa volume"], ["ko", "vs KO total volume"]].map(([id, lab]) => (
              <button key={id} onClick={() => setMetric(id)} style={{
                padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${metric === id ? T.accent : T.border}`,
                background: metric === id ? T.accentSoft : "transparent",
                color: metric === id ? T.accent : T.textDim }}>{lab}</button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 12, right: 20, bottom: 30, left: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
              <XAxis type="number" dataKey="x" tickFormatter={fmtVol}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                label={{ value: metric === "campa" ? "Campa volume →" : "KO total volume →",
                  position: "insideBottom", offset: -8, fill: T.textDim, fontSize: 10 }} />
              {/* Capped at 150%: a few markets have near-zero Campa volume and
                  produce 400-1000% ratios that flatten everything else. The
                  domain runs slightly past the cap so clamped bubbles sit
                  inside the plot area instead of being clipped by the top edge. */}
              <YAxis type="number" dataKey="y" domain={[0, 162]} allowDataOverflow
                ticks={[0, 25, 50, 75, 100, 125, 150]} tickFormatter={v => `${v}%`}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                label={{ value: "KO response ↑ (capped 150%)", angle: -90, position: "insideLeft",
                  offset: 10, fill: T.textDim, fontSize: 10 }} />
              <ZAxis type="number" dataKey="z" range={[70, 800]} />
              <ReferenceLine x={medCampa} stroke={T.textDim} strokeDasharray="4 4" />
              <ReferenceLine y={medResp * 100} stroke={T.textDim} strokeDasharray="4 4" />
              <Tooltip cursor={{ strokeDasharray: "3 3", stroke: T.accent }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
                      borderRadius: 8, padding: "10px 14px", fontSize: 11, boxShadow: T.shadowMd }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.market}</div>
                      <div style={{ color: T.textMid }}>Total KO volume: <b style={{ color: T.text }}>{fmtVol(d.koTotal)}</b></div>
                      <div style={{ color: T.textMid }}>Campa: <b style={{ color: T.text }}>{fmtVol(d.campa)}</b></div>
                      <div style={{ color: T.textMid }}>KO affordable: <b style={{ color: T.text }}>{fmtVol(d.koAff)}</b></div>
                      <div style={{ color: T.textMid }}>Response: <b style={{ color: T.text }}>{fmtPct(d.response, 0)}</b>
                        {d.yTrue > 150 && <span style={{ color: T.textDim, fontSize: 9.5 }}> (plotted at cap)</span>}</div>
                      <div style={{ color: T.textMid }}>Affordable mix: <b style={{ color: T.text }}>{fmtPct(d.affMix)}</b></div>
                    </div>
                  );
                }} />
              <Scatter data={scatter}>
                {scatter.map((d, i) => {
                  const big = d.x >= medCampa, weak = d.y < medResp * 100;
                  const fill = weak ? (big ? T.neg : T.warn) : (big ? T.pos : T.accent);
                  return <Cell key={i} fill={fill} fillOpacity={big && weak ? 0.8 : 0.5} stroke={fill} />;
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6, fontSize: 9.5, color: T.textDim }}>
            {[["Big Campa, weak response", T.neg], ["Small Campa, weak response", T.warn],
              ["Big Campa, strong response", T.pos], ["Small Campa, strong response", T.accent]].map(([l, c]) => (
              <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.7 }} />{l}
              </span>
            ))}
          </div>
        </Card>




      </div>


      {/* ── Affordable mix by market — full width, markets along the x-axis ── */}
      <Card title="Affordable Mix by Market" badge="RANKED"
        subtitle={`Share of KO volume that is affordable · ${month} · highest to lowest`}>
        <ResponsiveContainer width="100%" height={isMobile ? Math.max(320, markets.length * 22 + 40) : 320}>
          <BarChart data={[...markets].sort((a, b) => b.affMix - a.affMix)}
            layout={isMobile ? "vertical" : "horizontal"}
            margin={isMobile ? { top: 4, right: 12, bottom: 4, left: 0 } : { top: 22, right: 12, bottom: 8, left: 46 }}
            barCategoryGap="18%">
            <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={isMobile} horizontal={!isMobile} />
            {isMobile ? (<>
              <YAxis type="category" dataKey="market" interval={0} width={104}
                tickFormatter={v => (String(v).length > 16 ? `${String(v).slice(0, 15)}…` : v)}
                tick={{ fill: T.textMid, fontSize: 9 }} axisLine={{ stroke: T.border }} tickLine={false} />
              <XAxis type="number" tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                tick={{ fill: T.textDim, fontSize: 9 }} axisLine={{ stroke: T.border }} tickLine={false} />
            </>) : (<>
              <XAxis type="category" dataKey="market" interval={0}
                tick={{ fill: T.textMid, fontSize: 9.5 }} axisLine={{ stroke: T.border }}
                tickLine={false} angle={-40} textAnchor="end" height={78} />
              <YAxis type="number" tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }}
                tickLine={false} width={40} />
            </>)}
            <Tooltip content={<Tip pct />} cursor={{ fill: `${T.accent}08` }} />
            <Bar dataKey="affMix" name="Affordable mix" fill={T.pos} fillOpacity={0.85}
              radius={isMobile ? [0, 3, 3, 0] : [3, 3, 0, 0]}>
              <LabelList dataKey="affMix" position="top"
                formatter={v => `${(v * 100).toFixed(1)}%`}
                style={{ fill: T.textMid, fontSize: 9, fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Brand @10 — VPO and reach by state ── */}
      {brand && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: 16, boxShadow: T.shadow }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
            Brand @10 — Zero Sugar vs Campa
          </div>
          <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 2, marginBottom: 12 }}>
            Volume per outlet and weighted distribution by state
          </div>
          <BrandView data={brand} embedded />
        </div>
      )}

      {/* ── Join diagnostics ── */}
      <div style={{ background: T.bgAlt, border: `1px dashed ${T.borderMed}`, borderRadius: 10,
        padding: "10px 14px", fontSize: 10.5, color: T.textDim, lineHeight: 1.6 }}>
        <b style={{ color: T.textMid }}>Market join (BU Ship From → BU Revised):</b>{" "}
        {data.matched.length} BUs matched in both sheets.
        {data.unmappedPlants?.length > 0 && (
          <span style={{ color: T.neg }}>
            {" "}Unmapped KO plants: <b>{data.unmappedPlants.join(", ")}</b> — add a line to
            <code style={{ background: "#fff", padding: "1px 4px", borderRadius: 3, margin: "0 3px" }}>
              KO_PLANT_TO_BU
            </code>in koCampaParse.js to fold them into a BU.
          </span>
        )}
        {data.campaOnly?.length > 0 && (
          <span> Campa-only BUs: {data.campaOnly.map(k => data.label[k] || k).join(", ")}.</span>
        )}
        {data.fuzzyJoins?.length > 0 && (
          <span> Matched after stripping bottler prefix: {data.fuzzyJoins.join(", ")}.</span>
        )}
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: "pointer", color: T.textMid, fontWeight: 600 }}>
            Plant roll-up ({Object.keys(data.plantsByBU || {}).length} BUs)
          </summary>
          <div style={{ marginTop: 6, display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 4 }}>
            {Object.entries(data.plantsByBU || {}).sort().map(([bu, plants]) => (
              <div key={bu} style={{ fontSize: 10 }}>
                <b style={{ color: T.textMid }}>{bu}</b>{" ← "}
                {[...plants].join(" · ")}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

const thS = { padding: "8px 10px", fontSize: 9.5, textTransform: "uppercase",
  letterSpacing: 0.8, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" };
const tdS = { padding: "6px 10px", borderBottom: `1px solid ${T.border}`,
  textAlign: "right", color: T.text, whiteSpace: "nowrap" };


/* The market column is the only flexible one, so it must be capped — left
   to itself it absorbs every spare pixel and pushes the numbers off screen.
   The full name stays available via the cell's title attribute. */
const marketCell = {
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const allIndiaCell = {
  borderTop: `2px solid ${T.accent}`,
  borderBottom: `2px solid ${T.accent}`,
};

const thD = { padding: "8px 10px", fontSize: 9.5, textTransform: "uppercase",
  letterSpacing: 0.4, fontWeight: 700, textAlign: "right",
  whiteSpace: "normal", lineHeight: 1.35 };
const tdD = { padding: "4px 10px", borderBottom: `1px solid ${T.border}`,
  textAlign: "right", color: T.text, whiteSpace: "nowrap" };
const subHd = { fontSize: 8, fontWeight: 500, opacity: 0.7,
  textTransform: "none", letterSpacing: 0, marginTop: 2 };
const peakBox = { display: "inline-block", padding: "2px 14px", borderRadius: 5,
  border: "1.5px solid #E8A33D", background: "#FDF6E8", fontWeight: 700 };
const lg = { display: "inline-flex", alignItems: "center", gap: 6 };
const sw = { width: 11, height: 11, borderRadius: 2, display: "inline-block" };